<template>
  <ValidationObserver tag="form" ref="observer">
    <h2 class="title is-size-5-mobile">{{ $t('general.your-data') }}</h2>
    <Separator />
    <h3 class="title is-size-5">{{ $t('general.user-data') }}</h3>
    <div class="block">
      <ValidationProvider name="Herr/Frau" rules="oneOf:10,20" v-slot="{errors}">
        <fieldset aria-describedby="error-sex">
          <legend class="is-sr-only">{{ $t('general.salutation') }}</legend>
          <b-radio id="radioMale" v-model="customer.sex" name="sex" native-value="10">
            <span class="is-sr-only">{{ $t('general.salutation') }}</span>
            {{ $t('general.male') }}
          </b-radio>
          <b-radio id="radioFemale" v-model="customer.sex" name="sex" native-value="20">
            <span class="is-sr-only">{{ $t('general.salutation') }}</span>
            {{ $t('general.female') }}
          </b-radio>
          <p id="error-sex" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
        </fieldset>
      </ValidationProvider>
    </div>
    <div class="columns is-multiline">
      <div class="column is-5 is-12-desktop is-5-widescreen">
        <ValidationProvider :name="$t('general.firstname')" :rules="{required: true}" v-slot="{errors}">
          <b-field expanded label-position="inside" label-for="checkout-firstName">
            <template #label>
              {{ $t('general.firstname') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
            </template>
            <b-input aria-describedby="error-firstName" id="checkout-firstName" name="firstName" v-model="customer.firstName" autocomplete="given-name"></b-input>
            <p id="error-firstName" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
          </b-field>
        </ValidationProvider>
      </div>
      <div class="column is-7 is-12-desktop is-7-widescreen">
        <ValidationProvider :name="$t('general.lastname')" :rules="{required: true}" v-slot="{errors}">
          <b-field expanded label-position="inside" label-for="checkout-lastName">
            <template #label>
              {{ $t('general.lastname') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
            </template>
            <b-input aria-describedby="error-lastName" id="checkout-lastName" name="lastName" v-model="customer.lastName" autocomplete="family-name"></b-input>
            <p id="error-lastName" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
          </b-field>
        </ValidationProvider>
      </div>
      <div class="column is-7 is-12-desktop is-7-widescreen">
        <ValidationProvider :name="$t('general.email')" :rules="{required: true, email: true}" v-slot="{errors}">
          <b-field expanded label-position="inside" label-for="checkout-email">
            <template #label>
              {{ $t('general.email') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
            </template>
            <b-input aria-describedby="error-email" id="checkout-email" name="mail" v-model="customer.email" inputmode="email" autocomplete="email"></b-input>
            <p id="error-email" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
          </b-field>
        </ValidationProvider>
      </div>
      <div class="column is-5 is-12-desktop is-5-widescreen">
        <b-field :label="$t('general.phone')" expanded label-position="inside" label-for="checkout-phone">
          <b-input id="checkout-phone" v-model="customer.phone" inputmode="tel" autocomplete="tel"></b-input>
        </b-field>
      </div>
    </div>
    <Separator />
    <h3 class="title is-size-5">{{ $t('general.address-data') }}</h3>
    <div class="columns is-multiline">
      <div class="column is-7 is-12-desktop is-7-widescreen">
        <ValidationProvider :name="`${$t('general.street')} + ${$t('general.number')}`" :rules="{required: true}" v-slot="{errors}">
          <b-field label-position="inside" label-for="checkout-street">
            <template #label>
              {{ $t('general.street') }} & {{ $t('general.number') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
            </template>
            <b-input aria-describedby="error-street" id="checkout-street" name="streetInput" v-model="customer.address" autocomplete="address-line1"></b-input>
            <p id="error-street" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
          </b-field>
        </ValidationProvider>
      </div>
      <div class="column is-5 is-12-desktop is-5-widescreen">
        <ValidationProvider :name="$t('general.country')" :rules="{required: true}" v-slot="{errors}">
          <b-field label-position="inside" label-for="checkout-country">
            <template #label>
              {{ $t('general.country') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
            </template>
            <b-select aria-describedby="error-country" id="checkout-country" :placeholder="$t('general.country')" v-model="customer.country" icon="globe" expanded>
              <option v-for="option in isoCountries" :value="option.iso" :key="option.iso">{{ option.country }}</option>
            </b-select>
            <p id="error-country" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
          </b-field>
        </ValidationProvider>
      </div>
      <div class="column is-4">
        <ValidationProvider :name="$t('general.zip')" :rules="{required: true, numeric: true}" v-slot="{errors}">
          <b-field :label="`${$t('general.zip')}*`" label-position="inside" label-for="checkout-zip">
            <template #label>
              {{ $t('general.zip') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
            </template>
            <b-input aria-describedby="error-zip" id="checkout-zip" name="plz" v-model="customer.zip" inputmode="numeric" autocomplete="postal-code"></b-input>
            <p id="error-zip" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
          </b-field>
        </ValidationProvider>
      </div>
      <div class="column is-8">
        <ValidationProvider :name="$t('general.city')" :rules="{required: true}" v-slot="{errors}">
          <b-field label-position="inside" label-for="checkout-city">
            <template #label>
              {{ $t('general.city') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
            </template>
            <b-input aria-describedby="error-city" id="checkout-city" name="city" v-model="customer.city" autocomplete="address-level2"></b-input>
            <p id="error-city" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
          </b-field>
        </ValidationProvider>
      </div>
    </div>
    <small aria-hidden="true">(* {{ $t('general.required-field') }})</small>
  </ValidationObserver>
</template>

<script>
import Separator from '@/components/Separator'
import {mapGetters} from 'vuex'
export default {
  name: 'CustomerData',
  components: {
    Separator,
  },
  props: {
    customer: Object,
  },
  computed: {
    ...mapGetters({
      isoCountries: 'isoCountries',
    }),
  },
  async mounted() {
    const vf = this

    this.$watch(
      () => this.$refs.observer.errors,
      async () => {
        const isValid = await this.$refs.observer.validate({silent: true})
        vf.$emit('validated', isValid)
      },
    )
  },
  methods: {
    async runValidation() {
      const vf = this
      const isValid = await this.$refs.observer.validate()
      if (isValid) {
        vf.$emit('validated', isValid)
      }
    },
  },
}
</script>

<style scoped lang="scss"></style>
